import React, { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DIRECOES_CESSAO, TIPOS_MOVIMENTO, formatarMoeda, formatarNumero, labelTipoMovimento, lerNumero, sinalMovimento } from "../utils/societarioCalculos";
import {
  estiloBotaoAcao,
  estiloBotaoAdicionar,
  estiloCabecalhoInterno,
  estiloCampoInline,
  estiloCelulaInterna,
  estiloSelectInline,
} from "../utils/societarioEstilos";

// ==========================================================
// ABA MOVIMENTOS
// Tipo é lista fechada (regra 4). Cessão exige a direção.
// ==========================================================

const movimentoVazio = { socioId: null, tipo: "", direcao: "", quotas: "", valor: "" };

export default function SocietarioMovimentos({ movimentos, socios, somenteLeitura, onChange }) {
  const [novo, setNovo] = useState({ ...movimentoVazio });
  const [erro, setErro] = useState("");

  const nomeSocio = (id) => socios.find((s) => String(s.id) === String(id))?.nomeRazao || `Sócio #${id}`;

  const adicionar = () => {
    const quotas = lerNumero(novo.quotas);
    const valor = lerNumero(novo.valor);
    if (!novo.socioId) return setErro("Selecione o sócio.");
    if (!novo.tipo) return setErro("Selecione o tipo de movimento.");
    if (novo.tipo === "CESSAO" && !novo.direcao) return setErro("Informe se a cessão foi recebida ou cedida.");
    if (quotas === null || quotas <= 0) return setErro("Quotas devem ser maiores que zero.");
    if (valor === null || valor < 0) return setErro("Informe o valor em R$.");
    setErro("");
    onChange([
      ...movimentos,
      {
        id: `mov-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        socioId: novo.socioId,
        tipo: novo.tipo,
        direcao: novo.tipo === "CESSAO" ? novo.direcao : "",
        quotas: String(novo.quotas),
        valor: String(novo.valor),
      },
    ]);
    setNovo({ ...movimentoVazio });
  };

  const remover = (id) => onChange(movimentos.filter((m) => m.id !== id));

  const celulaNumero = { ...estiloCelulaInterna, textAlign: "right", whiteSpace: "nowrap" };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Alert severity="info">
        Movimentos de entrada (aumento de capital, compra, doação recebida, cessão recebida) somam ao sócio; os de saída subtraem. Transferências entre sócios
        precisam se compensar. Alteração apenas de redação pode ficar sem movimentos.
      </Alert>

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ width: "100%", minWidth: 820 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...estiloCabecalhoInterno, width: "38%" }}>Sócio</TableCell>
              <TableCell sx={estiloCabecalhoInterno}>Tipo</TableCell>
              <TableCell sx={{ ...estiloCabecalhoInterno, textAlign: "right" }}>Quotas</TableCell>
              <TableCell sx={{ ...estiloCabecalhoInterno, textAlign: "right" }}>Valor (R$)</TableCell>
              {!somenteLeitura && <TableCell sx={{ ...estiloCabecalhoInterno, textAlign: "right" }}>Ações</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {movimentos.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-666666)", fontStyle: "italic", py: 1.5, textAlign: "center" }}
                >
                  Nenhum movimento lançado.
                </TableCell>
              </TableRow>
            )}
            {movimentos.map((m) => {
              const sinal = sinalMovimento(m);
              return (
                <TableRow key={m.id} sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                  <TableCell sx={estiloCelulaInterna}>{nomeSocio(m.socioId)}</TableCell>
                  <TableCell sx={{ ...estiloCelulaInterna, color: sinal > 0 ? "#66bb6a" : "#ef5350", whiteSpace: "nowrap" }}>
                    {sinal > 0 ? "▲ " : "▼ "}
                    {labelTipoMovimento(m)}
                  </TableCell>
                  <TableCell sx={celulaNumero}>{formatarNumero(lerNumero(m.quotas), 6)}</TableCell>
                  <TableCell sx={celulaNumero}>{formatarMoeda(lerNumero(m.valor))}</TableCell>
                  {!somenteLeitura && (
                    <TableCell align="right" sx={estiloCelulaInterna}>
                      <Tooltip title="Remover movimento">
                        <IconButton size="small" onClick={() => remover(m.id)} sx={estiloBotaoAcao("#d32f2f", "#c62828")}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {!somenteLeitura && (
              <TableRow sx={{ backgroundColor: "var(--ig-f-1a1a1a)" }}>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 240 }}
                    options={socios.filter((s) => s.ativo)}
                    getOptionLabel={(s) => `${s.nomeRazao} (${s.documentoExibicao || s.documento})`}
                    value={socios.find((s) => String(s.id) === String(novo.socioId)) || null}
                    onChange={(_, val) => setNovo((p) => ({ ...p, socioId: val?.id ?? null }))}
                    renderInput={(params) => <TextField {...params} placeholder="Selecionar sócio" size="small" sx={estiloCampoInline} />}
                  />
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select
                        value={novo.tipo}
                        displayEmpty
                        onChange={(e) => setNovo((p) => ({ ...p, tipo: e.target.value, direcao: "" }))}
                        sx={estiloSelectInline}
                      >
                        <MenuItem value="">
                          <em>Tipo</em>
                        </MenuItem>
                        {TIPOS_MOVIMENTO.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {novo.tipo === "CESSAO" && (
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={novo.direcao} displayEmpty onChange={(e) => setNovo((p) => ({ ...p, direcao: e.target.value }))} sx={estiloSelectInline}>
                          <MenuItem value="">
                            <em>Direção</em>
                          </MenuItem>
                          {DIRECOES_CESSAO.map((d) => (
                            <MenuItem key={d.value} value={d.value}>
                              {d.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                  <TextField
                    size="small"
                    placeholder="Quotas"
                    value={novo.quotas}
                    onChange={(e) => setNovo((p) => ({ ...p, quotas: e.target.value }))}
                    slotProps={{ htmlInput: { inputMode: "decimal", style: { textAlign: "right" } } }}
                    sx={{ minWidth: 110, ...estiloCampoInline }}
                  />
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                  <TextField
                    size="small"
                    placeholder="R$"
                    value={novo.valor}
                    onChange={(e) => setNovo((p) => ({ ...p, valor: e.target.value }))}
                    slotProps={{ htmlInput: { inputMode: "decimal", style: { textAlign: "right" } } }}
                    sx={{ minWidth: 110, ...estiloCampoInline }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                  <Button size="small" startIcon={<AddIcon />} onClick={adicionar} sx={estiloBotaoAdicionar}>
                    Adicionar
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {erro && <Typography sx={{ color: "#f44336", fontSize: "0.8rem" }}>{erro}</Typography>}
    </Box>
  );
}
